import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { Panel, StatusChip } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { uploadPartnerDocument } from "../../api/endpoints/me";
import { documentRows, statusLabel, statusTone } from "./verificationDocuments";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf,.pdf,.jpg,.jpeg,.png";
const MAX_BYTES = 10 * 1024 * 1024;

const VerificationDocumentsPanel = ({ documents }) => {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [pending, setPending] = useState(null);

  const upload = useMutation(() => uploadPartnerDocument(pending.row.kind, pending.file), {
    onSuccess: () => {
      toast.success("Sent to the office for review");
      queryClient.invalidateQueries(partnerQueryKeys.me());
      setPending(null);
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not upload the document."
      ),
  });

  const choose = (row) => {
    setTarget(row);
    inputRef.current?.click();
  };

  const picked = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !target) return;
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} is too large. Maximum size is 10 MB.`);
      return;
    }
    setPending({ row: target, file });
  };

  return (
    <Panel title="Verification documents">
      <input ref={inputRef} type="file" accept={ACCEPT} className="sr-only" onChange={picked} />
      <ul className="list-none p-0 m-0">
        {documentRows(documents).map((row) => (
          <li
            key={row.kind}
            className="flex flex-wrap items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 m-0">
                {row.label}
                {row.required ? <sup className="text-rose-500"> *</sup> : null}
              </p>
              {row.document?.originalFilename ? (
                <p className="text-xs text-slate-500 m-0 mt-0.5">{row.document.originalFilename}</p>
              ) : null}
              {row.document?.status === "REJECTED" && row.document.rejectReason ? (
                <p className="text-xs text-rose-700 m-0 mt-1">
                  Reason: {row.document.rejectReason}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <StatusChip tone={statusTone(row.document?.status)}>
                {statusLabel(row.document?.status)}
              </StatusChip>
              {row.canUpload ? (
                <button
                  type="button"
                  onClick={() => choose(row)}
                  className="rounded-lg border border-teal-600 bg-white px-2.5 py-1 text-xs font-semibold text-teal-700 cursor-pointer hover:bg-teal-50"
                >
                  {row.document ? "Upload again" : "Upload"}
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={Boolean(pending)}
        title="Send document for review?"
        message={
          pending
            ? `${
                pending.file.name
              } will be sent to the office as your ${pending.row.label.toLowerCase()}. You are notified once they review it.`
            : ""
        }
        confirmLabel="Send"
        loading={upload.isLoading}
        onConfirm={() => upload.mutate()}
        onCancel={() => setPending(null)}
      />
    </Panel>
  );
};

export default VerificationDocumentsPanel;
